import React from 'react';
import { isFilterMultiSelectNone } from '../../components/filterMultiSelectUtils';
import {
  getDirection,
  getEffectiveMailStatus,
  MAILBOX_STATUSES,
  mergeFetchedInboxEmails,
  mergeInboxThreadListRow,
  inboxPrioSortRank,
  type DirectionTab,
  type InboxEmail,
  type SortMode,
} from './inboxShared';
import { inboxThreadKey } from '../../utils/inboxRoutes';
import {
  getCachedInboxEmails,
  setCachedInboxEmails,
} from '../../utils/inboxSessionCache';
import type { InboxLiveEvent, InboxLiveRow } from '../../hooks/useInboxLiveEmails';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const COUNTS_REFRESH_MS = 4000;

export type InboxDirectionCounts = { alle: number; in: number; out: number };

type UseInboxThreadPageOptions = {
  accountId: string | number | null;
  accountEmail: string;
  directionTab: DirectionTab;
  statusFilter: string[];
  searchTerm: string;
  sortMode: SortMode;
  folderExternalId?: string | null;
  starredOnly?: boolean;
  urgentOnly?: boolean;
  assignedUserId?: string | null;
  /** Ordner, deren Mails die aktuelle Liste ausblendet (Posteingang ohne Filter). */
  hiddenFolderIds?: ReadonlySet<string> | null;
};

function sitsInHiddenFolder(row: InboxEmail, hidden: ReadonlySet<string> | null | undefined): boolean {
  if (!hidden || hidden.size === 0) return false;
  return (row.provider_folder_ids ?? []).some((id) => hidden.has(id));
}

function parseListItems(data: unknown): InboxEmail[] {
  return (Array.isArray(data) ? data : []).map((m) => m as InboxEmail);
}

function isDefaultListView(opts: {
  directionTab: DirectionTab;
  statusFilter: string[];
  searchTerm: string;
  sortMode: SortMode;
}): boolean {
  return (
    opts.directionTab === 'alle' &&
    opts.statusFilter.length === 0 &&
    !opts.searchTerm.trim() &&
    opts.sortMode === 'datum'
  );
}

/** Ohne gewählten Status bleibt Spam aus dem Postfach. Die Spam-Ansicht setzt den Filter selbst. */
function statusesForList(statusFilter: string[], includeAll = false): string[] {
  if (isFilterMultiSelectNone(statusFilter)) return [];
  if (statusFilter.length > 0) return statusFilter;
  return includeAll ? [] : MAILBOX_STATUSES;
}

/** Live-Zeile darf in die aktuelle Listenansicht, ohne den Serverfilter zu verletzen. */
function liveRowMatchesCurrentView(
  slim: InboxEmail,
  opts: {
    directionTab: DirectionTab;
    statusFilter: string[];
    searchTerm: string;
    accountEmail: string;
    assignedUserId?: string | null;
    includeAllStatuses?: boolean;
    /** The thread is already listed: it stays, whichever way the new mail goes (like Gmail). */
    threadOnPage?: boolean;
  },
): boolean {
  if (
    !opts.threadOnPage &&
    opts.directionTab !== 'alle' &&
    getDirection(slim, opts.accountEmail) !== opts.directionTab
  ) {
    return false;
  }
  const statuses = statusesForList(opts.statusFilter, opts.includeAllStatuses);
  if (statuses.length > 0 && !statuses.includes(getEffectiveMailStatus(slim))) {
    return false;
  }
  if (opts.assignedUserId && String(slim.assigned_user_id ?? '') !== opts.assignedUserId) {
    return false;
  }
  const q = opts.searchTerm.trim().toLowerCase();
  if (q) {
    const hay = [slim.subject, slim.from_addr, slim.to_addr, slim.snippet].join(' ').toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

function slimLiveRow(row: InboxLiveRow): InboxEmail | null {
  if (row.id == null) return null;
  if (row.deleted_at || row.archived_at) return null;
  return {
    id: row.id as string,
    account_id: row.account_id as string,
    external_message_id: (row.external_message_id as string | null) ?? null,
    thread_id: (row.thread_id as string | null) ?? null,
    from_addr: String(row.from_addr ?? ''),
    to_addr: String(row.to_addr ?? ''),
    subject: String(row.subject ?? ''),
    date_at: row.date_at == null ? null : String(row.date_at),
    snippet: String(row.snippet ?? ''),
    lead_id: (row.lead_id as string | null) ?? null,
    classification: (row.classification as InboxEmail['classification']) ?? null,
    classification_confidence:
      typeof row.classification_confidence === 'number' ? row.classification_confidence : null,
    needs_reply: typeof row.needs_reply === 'boolean' ? row.needs_reply : null,
    urgency_level: (row.urgency_level as InboxEmail['urgency_level']) ?? null,
    is_urgent: typeof row.is_urgent === 'boolean' ? row.is_urgent : null,
    assigned_user_id: row.assigned_user_id == null || row.assigned_user_id === '' ? null : String(row.assigned_user_id),
    starred: typeof row.starred === 'boolean' ? row.starred : false,
    is_unread: typeof row.is_unread === 'boolean' ? row.is_unread : false,
    provider_folder_ids: Array.isArray(row.provider_folder_ids) ? (row.provider_folder_ids as string[]) : [],
    pending_folder_id: row.pending_folder_id == null || row.pending_folder_id === '' ? null : String(row.pending_folder_id),
    ai_last_evaluated_at: row.ai_last_evaluated_at == null ? null : String(row.ai_last_evaluated_at),
    status: String(row.classification || 'other') as InboxEmail['status'],
    has_attachments: Array.isArray(row.attachments_json)
      ? row.attachments_json.length > 0
      : false,
  };
}

function liveRowBelongsToAccount(row: InboxLiveRow, accountId: string | number): boolean {
  if (row.account_id == null || row.account_id === '') return true;
  return String(row.account_id) === String(accountId);
}

/** INSERT/upsert payloads must carry list fields; PK-only UPDATE rows cannot be inserted. */
function slimLiveRowCanInsert(row: InboxEmail): boolean {
  return Boolean(row.from_addr || row.subject || row.date_at);
}

function dateMs(d: string | null | undefined): number {
  return d ? new Date(d).getTime() : 0;
}

export function useInboxThreadPage({
  accountId,
  accountEmail,
  directionTab,
  statusFilter,
  searchTerm,
  sortMode,
  folderExternalId = null,
  starredOnly = false,
  urgentOnly = false,
  assignedUserId = null,
  hiddenFolderIds = null,
}: UseInboxThreadPageOptions) {
  const [items, setItems] = React.useState<InboxEmail[]>([]);
  const [counts, setCounts] = React.useState<InboxDirectionCounts>({ alle: 0, in: 0, out: 0 });
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [hasMore, setHasMore] = React.useState(false);
  const [pageIndex, setPageIndex] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [silentRefreshing, setSilentRefreshing] = React.useState(false);
  const [loadedAccountId, setLoadedAccountId] = React.useState<string | number | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const [debouncedSearch, setDebouncedSearch] = React.useState(searchTerm);
  React.useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchTerm), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchTerm]);

  const genRef = React.useRef(0);
  const inFlightRef = React.useRef(false);
  const pendingSilentRef = React.useRef(false);
  const nextCursorRef = React.useRef<string | null>(null);
  const hasMoreRef = React.useRef(false);
  const pageIndexRef = React.useRef(0);
  const pageCursorsRef = React.useRef<(string | null)[]>([null]);
  const countsTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const itemsRef = React.useRef(items);
  itemsRef.current = items;
  nextCursorRef.current = nextCursor;
  hasMoreRef.current = hasMore;
  pageIndexRef.current = pageIndex;

  const accountIdRef = React.useRef(accountId);
  accountIdRef.current = accountId;
  const accountEmailRef = React.useRef(accountEmail);
  accountEmailRef.current = accountEmail;
  const hiddenFolderIdsRef = React.useRef(hiddenFolderIds);
  hiddenFolderIdsRef.current = hiddenFolderIds;

  const filterKey = `${accountId ?? ''}|${directionTab}|${statusFilter.join(',')}|${debouncedSearch}|${sortMode}|${folderExternalId ?? ''}|${starredOnly ? 1 : 0}|${urgentOnly ? 1 : 0}|${assignedUserId ?? ''}`;
  const filterKeyRef = React.useRef(filterKey);
  filterKeyRef.current = filterKey;

  const statusNone = isFilterMultiSelectNone(statusFilter);
  const statusKey = statusFilter.join('\0');
  const statusFilterRef = React.useRef(statusFilter);
  statusFilterRef.current = statusFilter;

  const includeAllStatuses = Boolean(folderExternalId || starredOnly);

  const buildUrl = React.useCallback(
    (id: string | number, cursor: string | null, opts?: { refresh?: boolean }) => {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      if (cursor) params.set('cursor', cursor);
      if (directionTab !== 'alle') params.set('direction', directionTab);
      if (sortMode === 'prio') params.set('sort', 'prio');
      const q = debouncedSearch.trim();
      if (q) params.set('q', q);
      for (const s of statusesForList(statusFilterRef.current, includeAllStatuses)) params.append('status', s);
      if (folderExternalId) params.set('folder', folderExternalId);
      if (starredOnly) params.set('starred', '1');
      if (urgentOnly) params.set('urgent', '1');
      if (assignedUserId) params.set('assignee', assignedUserId);
      if (opts?.refresh) params.set('refresh', '1');
      return `/api/inbox/accounts/${id}/emails?${params.toString()}`;
    },
    [assignedUserId, debouncedSearch, directionTab, folderExternalId, includeAllStatuses, sortMode, starredOnly, statusKey, urgentOnly],
  );

  const applyPagePayload = React.useCallback(
    (
      data: {
        items?: unknown;
        counts?: InboxDirectionCounts | null;
        next_cursor?: string | null;
        has_more?: boolean;
      },
      opts: { reset: boolean; accountId: string | number },
    ) => {
      const fetched = parseListItems(data.items);
      const merged = mergeFetchedInboxEmails(
        [],
        fetched,
        accountEmailRef.current,
        opts.accountId,
      );
      setItems(merged);
      if (
        opts.reset &&
        !folderExternalId &&
        !starredOnly &&
        !urgentOnly &&
        isDefaultListView({ directionTab, statusFilter: statusFilterRef.current, searchTerm: debouncedSearch, sortMode })
      ) {
        setCachedInboxEmails(opts.accountId, merged);
      }
      if (opts.reset && data.counts) {
        setCounts({
          alle: Number(data.counts.alle ?? 0),
          in: Number(data.counts.in ?? 0),
          out: Number(data.counts.out ?? 0),
        });
      }
      setNextCursor(data.next_cursor ?? null);
      setHasMore(Boolean(data.has_more));
      setLoadedAccountId(opts.accountId);
    },
    [debouncedSearch, directionTab, sortMode, statusKey, folderExternalId, starredOnly, urgentOnly],
  );

  const loadPage = React.useCallback(
    async (options?: { reset?: boolean; silent?: boolean; page?: number }): Promise<void> => {
      const id = accountIdRef.current;
      if (id == null) {
        setItems([]);
        setLoadedAccountId(null);
        setLoading(false);
        setHasMore(false);
        setNextCursor(null);
        setPageIndex(0);
        return;
      }
      if (statusNone) {
        setItems([]);
        setCounts({ alle: 0, in: 0, out: 0 });
        setHasMore(false);
        setNextCursor(null);
        setPageIndex(0);
        setLoadedAccountId(id);
        setLoading(false);
        return;
      }

      const reset = options?.reset !== false && options?.page == null;
      const page = reset ? 0 : Math.max(0, options?.page ?? 0);
      const silent = options?.silent === true;
      if (!reset && page > 0 && pageCursorsRef.current[page] == null && !nextCursorRef.current) return;

      if (silent && inFlightRef.current) {
        pendingSilentRef.current = true;
        return;
      }

      const gen = ++genRef.current;
      if (reset) pageCursorsRef.current = [null];
      const cursor = pageCursorsRef.current[page] ?? (page === pageIndexRef.current + 1 ? nextCursorRef.current : null);
      inFlightRef.current = true;
      if (reset) {
        if (silent) setSilentRefreshing(true);
        else setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const refreshFolder = reset && !silent && !cursor && (Boolean(folderExternalId) || starredOnly);
        const res = await fetch(buildUrl(id, cursor, { refresh: refreshFolder }));
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'E-Mails konnten nicht geladen werden.');
        if (gen !== genRef.current) return;
        if (String(accountIdRef.current ?? '') !== String(id)) return;
        applyPagePayload(data, { reset, accountId: id });
        if (data?.next_cursor) pageCursorsRef.current[page + 1] = data.next_cursor;
        setPageIndex(page);
        setError(null);
      } catch (err) {
        if (gen !== genRef.current) return;
        if (!silent && reset) {
          setItems([]);
          setLoadedAccountId(id);
          setError(err instanceof Error ? err.message : 'E-Mails konnten nicht geladen werden.');
        }
        throw err;
      } finally {
        inFlightRef.current = false;
        if (reset) {
          if (silent) {
            setSilentRefreshing((prev) =>
              accountIdRef.current != null && String(accountIdRef.current) === String(id) ? false : prev,
            );
          } else {
            setLoading(false);
          }
        } else {
          setLoadingMore(false);
        }
        if (pendingSilentRef.current) {
          pendingSilentRef.current = false;
          void loadPage({ reset: true, silent: true });
        }
      }
    },
    [applyPagePayload, buildUrl, folderExternalId, starredOnly, statusNone],
  );

  const goNext = React.useCallback(() => {
    if (!hasMoreRef.current || !nextCursorRef.current) return;
    const page = pageIndexRef.current + 1;
    pageCursorsRef.current[page] = nextCursorRef.current;
    void loadPage({ reset: false, page }).catch(() => undefined);
  }, [loadPage]);

  const goPrev = React.useCallback(() => {
    const page = pageIndexRef.current - 1;
    if (page < 0) return;
    void loadPage({ reset: false, page }).catch(() => undefined);
  }, [loadPage]);

  const reload = React.useCallback(
    (options?: { silent?: boolean }) => loadPage({ reset: true, silent: options?.silent }),
    [loadPage],
  );

  React.useEffect(() => {
    if (!accountId) {
      setItems([]);
      setLoadedAccountId(null);
      setLoading(false);
      setSilentRefreshing(false);
      setHasMore(false);
      setNextCursor(null);
      setPageIndex(0);
      return;
    }

    const defaultView = isDefaultListView({
      directionTab,
      statusFilter,
      searchTerm: debouncedSearch,
      sortMode,
    });
    const cached = defaultView ? getCachedInboxEmails(accountId) : null;
    const visibleCached = cached?.filter((mail) => getEffectiveMailStatus(mail) !== 'spam') ?? null;
    if (visibleCached && visibleCached.length > 0) {
      setItems(visibleCached);
      setLoadedAccountId(accountId);
      void reload({ silent: true }).catch(() => undefined);
      return;
    }

    setItems([]);
    setLoadedAccountId(null);
    void reload().catch(() => undefined);
  }, [accountId, debouncedSearch, directionTab, reload, sortMode, statusKey, folderExternalId, starredOnly, urgentOnly, assignedUserId]);

  const refreshCountsSoon = React.useCallback(() => {
    if (countsTimerRef.current) return;
    const keyAtSchedule = filterKeyRef.current;
    countsTimerRef.current = setTimeout(() => {
      countsTimerRef.current = null;
      const id = accountIdRef.current;
      if (id == null || statusNone) return;
      void fetch(buildUrl(id, null))
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (!data?.counts) return;
          if (filterKeyRef.current !== keyAtSchedule) {
            return;
          }
          setCounts({
            alle: Number(data.counts.alle ?? 0),
            in: Number(data.counts.in ?? 0),
            out: Number(data.counts.out ?? 0),
          });
        })
        .catch(() => {
          /* keep stale counts */
        });
    }, COUNTS_REFRESH_MS);
  }, [buildUrl, debouncedSearch, directionTab, sortMode, statusFilter, statusNone]);

  React.useEffect(
    () => () => {
      if (countsTimerRef.current) clearTimeout(countsTimerRef.current);
    },
    [],
  );

  const applyLiveRow = React.useCallback(
    (event: InboxLiveEvent, row: InboxLiveRow) => {
      const id = accountIdRef.current;
      if (id == null) return;
      if (statusNone) return;
      if (!liveRowBelongsToAccount(row, id)) return;

      if (event === 'UPDATE' && (row.deleted_at || row.archived_at)) {
        const rowKey = inboxThreadKey({
          id: row.id as string | number,
          thread_id: (row.thread_id as string | null) ?? null,
        });
        const onPage = itemsRef.current.some(
          (m) => String(m.id) === String(row.id) || inboxThreadKey(m) === rowKey,
        );
        if (onPage) {
          setItems((prev) => {
            const existing = prev.find(
              (m) => String(m.id) === String(row.id) || inboxThreadKey(m) === rowKey,
            );
            if (!existing) return prev;
            if ((existing.thread_count || 1) <= 1) {
              return prev.filter((m) => inboxThreadKey(m) !== rowKey);
            }
            return prev;
          });
          void reload({ silent: true });
        }
        refreshCountsSoon();
        return;
      }

      const slim = slimLiveRow(row);
      if (!slim) return;

      const existingById = itemsRef.current.some((m) => String(m.id) === String(slim.id));
      if (event === 'UPDATE' && existingById) {
        setItems((prev) => {
          const allowed = statusesForList(statusFilter, includeAllStatuses);
          if (allowed.length > 0 && !allowed.includes(getEffectiveMailStatus(slim))) {
            return prev.filter((m) => String(m.id) !== String(slim.id));
          }
          if (assignedUserId && String(slim.assigned_user_id ?? '') !== assignedUserId) {
            return prev.filter((m) => String(m.id) !== String(slim.id));
          }
          // Filed by the AI or in another tab: it leaves the Posteingang like a manual move.
          if (sitsInHiddenFolder(slim, hiddenFolderIdsRef.current)) {
            return prev.filter((m) => String(m.id) !== String(slim.id));
          }
          return prev.map((m) =>
            String(m.id) === String(slim.id)
              ? {
                  ...m,
                  ...slim,
                  body_html: m.body_html,
                  body_text: m.body_text,
                  attachments: m.attachments,
                  attachments_json: m.attachments_json,
                  thread_count: m.thread_count,
                }
              : m,
          );
        });
        refreshCountsSoon();
        return;
      }

      if (!slimLiveRowCanInsert(slim)) return;
      if (sitsInHiddenFolder(slim, hiddenFolderIdsRef.current)) return;
      if (pageIndexRef.current !== 0) {
        refreshCountsSoon();
        return;
      }

      const existingThread = itemsRef.current.find((m) => inboxThreadKey(m) === inboxThreadKey(slim));
      if (
        !liveRowMatchesCurrentView(slim, {
          directionTab,
          statusFilter,
          searchTerm: debouncedSearch,
          accountEmail: accountEmailRef.current,
          assignedUserId,
          includeAllStatuses,
          threadOnPage: Boolean(existingThread),
        })
      ) {
        refreshCountsSoon();
        return;
      }

      const last = itemsRef.current[itemsRef.current.length - 1];
      if (
        !existingThread &&
        hasMoreRef.current &&
        last &&
        slim.date_at &&
        dateMs(slim.date_at) < dateMs(last.date_at)
      ) {
        return;
      }

      setItems((prev) => {
        const merged = mergeInboxThreadListRow(prev, slim);
        merged.sort((a, b) => {
          if (sortMode === 'prio') {
            const rank = inboxPrioSortRank(a) - inboxPrioSortRank(b);
            if (rank !== 0) return rank;
          }
          return dateMs(b.date_at) - dateMs(a.date_at);
        });
        return merged.length > PAGE_SIZE ? merged.slice(0, PAGE_SIZE) : merged;
      });
      if (existingThread) {
        refreshCountsSoon();
        return;
      }
      const dir = getDirection(slim, accountEmailRef.current);
      setCounts((prev) => ({
        alle: prev.alle + 1,
        in: prev.in + (dir === 'in' ? 1 : 0),
        out: prev.out + (dir === 'out' ? 1 : 0),
      }));
    },
    [assignedUserId, debouncedSearch, directionTab, includeAllStatuses, refreshCountsSoon, reload, sortMode, statusFilter, statusNone],
  );

  return {
    items,
    setItems,
    counts,
    hasMore,
    pageIndex,
    pageSize: PAGE_SIZE,
    loading,
    loadingMore,
    silentRefreshing,
    loadedAccountId,
    error,
    setError,
    goNext,
    goPrev,
    reload,
    applyLiveRow,
  };
}
